/**
 * Разворачивание выбранного в плоский список файлов.
 *
 * Комплект проектной документации приходит папкой на сотни файлов, а
 * `dataTransfer.files` папок не отдаёт вовсе — брошенная папка молча
 * превращалась в пустой список и юрист не понимал, что произошло.
 */

/** `accept` у нас всегда список расширений (не MIME) — см. `fileAccept.ts`. */
export function matchesAccept(fileName: string, accept: string): boolean {
  if (!accept) return true;
  const name = fileName.toLowerCase();
  return accept
    .split(',')
    .map((ext) => ext.trim().toLowerCase())
    .filter(Boolean)
    .some((ext) => name.endsWith(ext));
}

/**
 * Служебный мусор ОС и офисных пакетов. В папке с ПД он есть всегда, а грузить
 * его на сервер и показывать юристу в списке комплекта незачем.
 */
function isJunk(fileName: string): boolean {
  const name = fileName.toLowerCase();
  return (
    name === '.ds_store' ||
    name === 'thumbs.db' ||
    name === 'desktop.ini' ||
    name.startsWith('~$') // временные файлы Word/Excel
  );
}

function readFile(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

/**
 * `readEntries` отдаёт порциями (в Chrome — по 100) и сигналит о конце пустым
 * массивом. Без цикла папка на 210 файлов молча обрежется до первой сотни.
 */
function readBatch(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

async function walkEntry(entry: FileSystemEntry, out: File[]): Promise<void> {
  if (entry.isFile) {
    const file = await readFile(entry as FileSystemFileEntry);
    if (!isJunk(file.name)) out.push(file);
    return;
  }
  if (!entry.isDirectory) return;

  const reader = (entry as FileSystemDirectoryEntry).createReader();
  for (;;) {
    const batch = await readBatch(reader);
    if (batch.length === 0) break;
    for (const child of batch) {
      await walkEntry(child, out);
    }
  }
}

/**
 * Плоский список файлов из drop'а, с рекурсивным обходом папок.
 *
 * `items` живёт только до первого `await`, поэтому синхронно снимаем entries и
 * лишь потом обходим. Браузер при drop не применяет `accept` — фильтруем сами,
 * иначе из папки ПД приедут посторонние форматы.
 */
export async function filesFromDrop(dt: DataTransfer, accept: string): Promise<File[]> {
  const entries = Array.from(dt.items)
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null)
    .filter((entry): entry is FileSystemEntry => entry !== null);

  if (entries.length === 0) {
    // Старый браузер или источник без entries — берём что дал, папок там нет.
    return Array.from(dt.files).filter((f) => !isJunk(f.name) && matchesAccept(f.name, accept));
  }

  const out: File[] = [];
  for (const entry of entries) {
    await walkEntry(entry, out);
  }
  return out.filter((f) => matchesAccept(f.name, accept));
}

/** Выбор через диалог: `webkitdirectory` уже отдаёт папку развёрнутой. */
export function filesFromInput(list: FileList | null, accept: string): File[] {
  if (!list) return [];
  return Array.from(list).filter((f) => !isJunk(f.name) && matchesAccept(f.name, accept));
}
